<template>
  <div class="pdf-wrapper">
    <div class="pt-1 report-header d-flex align-center">
      <div class="col-6">
        <div class="pl-4 header-info-row text-left">
          <div class="header-item" v-if="reportData.farm_name">
            <div class="header-value">{{ reportData.farm_name }}</div>
            <div class="header-subtitle">Farm</div>
          </div>
          <div class="header-item">
            <div class="header-value">{{ reportData.client_name }}</div>
            <div class="header-subtitle">Client</div>
          </div>
          <div class="header-item">
            <div class="header-value">{{ reportData.corporation_name }}</div>
            <div class="header-subtitle">Organization</div>
          </div>
        </div>
      </div>
      <div class="col-6 text-right pr-3">
        <div class="sub-title text-right pr-1">
          Year {{ reportData.year }} | {{ reportData.total_acreage }} ac,
          {{ Object.keys(reportData.field_metadata).length }} fields | Generated on {{ new Date().toLocaleDateString() }}
        </div>
      </div>
    </div>

    <div class="row pt-1 d-flex align-items-center">
      <div class="col-6">
        <div class="arva-serif report-title">Grower Report</div>
      </div>
      <div class="col-6 text-right">
        <img
          class="top-grower-logo mr-2"
          src="/assets/images/logos/arva-logo-new.png"
          alt="Arva"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-12 pt-0">
        <div class="map-wrapper px-2">
          <div id="mapbox-map" class="fill-wrapper"></div>
        </div>
      </div>
    </div>

    <!-- Grower Acreage Section -->
    <div class="row">
      <div class="col-12 pl-5 pr-5 pt-0">
        <div class="section-title mb-1">Grower Acreage</div>
        <div class="row">
          <!-- Left: Acreage by Region  -->
          <div class="col-6 pr-3">
            <h4 class="subtitle-1 font-weight-bold mb-2">Acreage by Region</h4>
            <table class="acreage-table">
              <thead>
                <tr>
                  <th class="region-header">Region</th>
                  <th class="acres-header">Acres</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="region in acreageByRegions" :key="region.geoid_id">
                  <td class="region-column">{{ region.county_name }}, {{ region.state_name }}, {{ region.country_name }}</td>
                  <td class="acres-column">{{ region.acreage | prettyInteger }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Right: Harvest Matrix -->
          <div class="col-6 pl-3">
            <h4 class="subtitle-1 font-weight-bold mb-2">Acreage by Harvest {{ reportData.year }}</h4>
            <div v-if="hasHybridData">
              <table class="crop-table">
                <thead>
                  <tr>
                    <th class="region-header">Region</th>
                    <th v-for="crop in harvestMatrix.crops"
                        :key="crop.cropId"
                        class="acres-header"
                        style="text-align:right">
                      {{ crop.cropName }} ac / # fields
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="region in harvestMatrix.regions" :key="region.geoid_id">
                    <td class="region-column">
                      {{ region.county_name }}, {{ region.state_name }}, {{ region.country_name }}
                    </td>
                    <td v-for="crop in harvestMatrix.crops"
                        :key="crop.cropId"
                        class="acres-column">
                      {{ getAcreageForRegionCrop(region.geoid_id, crop.cropId) | prettyInteger }} / {{ getFieldCountForRegionCrop(region.geoid_id, crop.cropId) }}
                    </td>
                  </tr>
                  <tr class="table-total">
                    <td class="region-column font-weight-bold">Total</td>
                    <td v-for="crop in harvestMatrix.crops"
                        :key="crop.cropId"
                        class="acres-column font-weight-bold">
                      {{ crop.totalAcreage | prettyInteger }} / {{ crop.cropFieldCount }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="mt-2 body-2">
                <div>
                  {{ fieldsWithHarvestCount }} out of {{ totalFieldCount }} fields
                  ({{ acresWithHarvest | prettyInteger }} out of {{ reportData.total_acreage | prettyInteger }} total acres)
                  have reported harvest data for year {{ reportData.year }}.
                </div>
                <div class="mt-1">
                  {{ harvestDataStats.manual }} fields ({{ manualDataPercentage }}%) had manually input harvest data,
                  while {{ harvestDataStats.machine }} fields ({{ machineDataPercentage }}%) had machine-sourced harvest data.
                </div>
              </div>
            </div>
            <div v-else class="body-2">
              No harvest data available. Harvest data needs to be added first to display acreage by crop information.
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row avoid-break">
      <div class="col-12 pl-5 pr-5">
        <div class="section-title">Soil Survey</div>
        <div v-if="Object.keys(soilData.fieldTextures).length === 0">
          Soil data not currently available.
        </div>
        <div v-else class="body-2 section-text mb-2">
          Soil data estimated from {{ soilStatsSource }}.
        </div>
        <div class="row">
          <div class="col-6">
            <div class="subtitle-2 mb-2">Soil Texture for Fields</div>
            <SoilTextureTriangle :samplePoints="soilTrianglePoints" />
          </div>
          <div class="col-6 d-flex flex-column" style="height: 100%;">
            <div class="subtitle-2 mb-1">Farm-wide Soil Statistics</div>
            <table class="soil-table mt-4">
              <thead>
                <tr>
                  <th></th>
                  <th>Average</th>
                  <th>Median</th>
                  <th>Q1</th>
                  <th>Q3</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>CEC</td>
                  <td>{{ formatSoilStat(soilData.growerStats.cec, 'average') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.cec, 'median') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.cec, 'q1') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.cec, 'q3') }}</td>
                </tr>
                <tr>
                  <td>SOC (est.)</td>
                  <td>{{ formatSoilStat(soilData.growerStats.soc, 'average') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.soc, 'median') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.soc, 'q1') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.soc, 'q3') }}</td>
                </tr>
                <tr>
                  <td>Silt%</td>
                  <td>{{ formatSoilStat(soilData.growerStats.silt_pct, 'average') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.silt_pct, 'median') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.silt_pct, 'q1') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.silt_pct, 'q3') }}</td>
                </tr>
                <tr>
                  <td>Clay%</td>
                  <td>{{ formatSoilStat(soilData.growerStats.clay_pct, 'average') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.clay_pct, 'median') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.clay_pct, 'q1') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.clay_pct, 'q3') }}</td>
                </tr>
                <tr>
                  <td>Sand%</td>
                  <td>{{ formatSoilStat(soilData.growerStats.sand_pct, 'average') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.sand_pct, 'median') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.sand_pct, 'q1') }}</td>
                  <td>{{ formatSoilStat(soilData.growerStats.sand_pct, 'q3') }}</td>
                </tr>
              </tbody>
            </table>
            <div class="body-2 pt-2">Statistics across all fields on farm <i>{{ farmDisplayName }}</i>.
              For each metric, the first quartile (Q1) value is from the field at the 25th percentile, and the third quartile (Q3) is from the field at the 75th percentile.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- <div class="row">
      <div class="col-12 pl-5 pr-5">
        <div class="section-title mb-1">Revenue & ROI Estimates</div>
        <div class="body-2">🚧</div>
      </div>
    </div> -->

    <div class="row">
      <div class="col-12 pl-5 pr-5 pt-0">
        <div class="section-title mb-1 pt-0">Yield By Reported Hybrids on Farm</div>
        <!-- Per‑crop charts / errors -->
        <div v-if="!hasHybridData" class="body-2">
          No hybrid yield data available. Confirmed harvest data is required.
        </div>
        <div v-else>
          <div class="body-2 section-text">
            Showing reported hybrid yields for each crop harvested on farm <i>{{ farmDisplayName }}</i> in {{ reportData.year }}.
          </div>

          <div class="d-flex avoid-break justify-center my-4">
            <div class="boxplot-tutorial-box pa-3">
              <div class="boxplot-tutorial-title mb-2">ⓘ Boxplots for farm data</div>
              <div class="boxplot-tutorial-content">
                <div class="mb-2">A boxplot shows you where most of your data lies (the box), the middle (the median), and how spread out it is (the whiskers).</div>
                <div class="mb-2">A boxplot (also called a box-and-whisker plot) is a way to show how data is spread out. It summarizes key statistics of a dataset in a compact visual form:</div>
                <ul class="boxplot-tutorial-list">
                  <li><strong>Median (Q2):</strong> The middle value of the data. Half the values are below it, and half are above it.</li>
                  <li><strong>Q1 (First Quartile):</strong> The value at the 25th percentile. This means 25% of the data is below Q1.</li>
                  <li><strong>Q3 (Third Quartile):</strong> The value at the 75th percentile. This means 75% of the data is below Q3.</li>
                  <li><strong>Interquartile Range (IQR):</strong> The distance between Q3 and Q1 (IQR = Q3 – Q1). This shows the spread of the middle 50% of the data.</li>
                  <li><strong>Box:</strong> Drawn from Q1 to Q3. It contains the middle half of the data.</li>
                  <li><strong>Line inside the box:</strong> Marks the median (Q2).</li>
                  <li><strong>Whiskers:</strong> Lines that extend from the box to show the smallest and largest values.</li>
                </ul>
                <div class="mb-2">If all reported yields for a given hybrid are the same, the boxplot will appear as a single line.</div>
              </div>
            </div>
          </div>
          <div
            v-for="crop in yieldsByCrop"
            :key="crop.cropName"
            class="mx-5 avoid-break"
          >
            <div class="subtitle-1 font-weight-bold mb-1">
              {{ crop.cropName }}
            </div>
            <div v-if="crop.unitError" class="body-2 text--error">
              {{ crop.unitError }}
            </div>
            <div v-else-if="crop.hybrids.length === 0" class="body-2">
              No reported hybrid yield data available for {{ crop.cropName }}.
            </div>
            <div v-else class="row hybrid-chart-row">
              <div class="col-7">
                <div class="axis-label ml-1">Hybrid name</div>
                <apexchart
                  :options="hybridChartOptionsForCrop(crop)"
                  :series="hybridChartSeriesForCrop(crop)"
                  type="boxPlot"
                  :height="hybridChartHeight(crop)"
                />
              </div>
              <div class="col-5">
                <table class="hybrid-stats-table">
                  <thead>
                    <tr>
                      <th>Hybrid</th>
                      <th>Acreage</th>
                      <th>Avg</th>
                      <th>Median</th>
                      <th>Q1</th>
                      <th>Q3</th>
                      <th>Min</th>
                      <th>Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="h in crop.hybrids" :key="h.hybridName">
                      <td class="h-name">{{ h.hybridName }}</td>
                      <td>{{ h.acreage | prettyInteger }} ac</td>
                      <td>{{ h.stats.avg.toFixed(0) }}</td>
                      <td>{{ h.stats.median.toFixed(0) }}</td>
                      <td>{{ h.stats.q1.toFixed(0) }}</td>
                      <td>{{ h.stats.q3.toFixed(0) }}</td>
                      <td>{{ h.stats.min.toFixed(0) }}</td>
                      <td>{{ h.stats.max.toFixed(0) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12 pl-5 pr-5">
        <div class="section-title mb-1">Arva Ground Types (AGT) </div>
        <div class="body-2 section-text">
          Arva Ground Types are a soil classification based on soil survey data, terrain, climate, remote sensing, and other factors.
        </div>
        <div class="subtitle-2 mb-2 mt-2">
          Farm AGT Breakdown
        </div>
        <div class="body-2">
          Soil survey data and acreage for the top AGTs by acreage on farm <i>{{ farmDisplayName }}</i>.
        </div>
        <div v-if="agtSoilBreakdown.length" class="agt-breakdown-chips d-flex flex-wrap mt-2 mb-3">
          <div
            v-for="agt in agtSoilBreakdown"
            :key="agt.agt"
            class="agt-chip agt-breakdown-chip ma-1 avoid-break"
            :style="{ backgroundColor: getAgtBackgroundColor(agt.agt) }"
          >
            <div class="d-flex justify-space-between agt-breakdown-header">
              <div class="agt-chip-label">AGT {{ agt.shortAgt }}</div>
              <div class="agt-chip-acres">{{ agt.acreage | prettyInteger }} ac</div>
            </div>
            <div class="agt-breakdown-stats mt-1">
              <div class="agt-breakdown-stat">
                <span class="label">Avg CEC (est.)</span>
                <span class="value">{{ formatNumber(agt.cecAvg, 1) }}</span>
              </div>
              <div class="agt-breakdown-stat">
                <span class="label">Avg SOC (est.)</span>
                <span class="value">{{ formatNumber(agt.omAvg, 2) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="body-2 mb-2">No AGT soil statistics available.</div>
        <div class="subtitle-2 mb-2 mt-1">
          AGT Yield by Crop on Farm
        </div>
        <div v-if="agtYieldsByCrop.length === 0" class="body-2">
          No AGT yield data available: machine-sourced yield data is required.
        </div>
        <div v-else>
          <div
            v-for="crop in agtYieldsByCrop"
            :key="crop.cropId"
            class="mx-5 mb-4 avoid-break"
          >
            <div class="subtitle-1 font-weight-bold mb-1">
              {{ crop.cropName }}
            </div>
            <div v-if="crop.unitError" class="body-2 text--error">
              {{ crop.unitError }}
            </div>
            <div v-else class="row agt-chart-row">
              <div class="col-7">
                <div class="axis-label ml-1">Arva Ground Type</div>
                <apexchart
                  :options="agtChartOptionsForCrop(crop)"
                  :series="agtChartSeriesForCrop(crop)"
                  type="boxPlot"
                  :height="agtChartHeight(crop)"
                />
              </div>
              <div class="col-5">
                <div class="d-flex flex-wrap">
                  <div
                    v-for="a in crop.agts"
                    :key="a.agt"
                    class="agt-chip ma-1"
                    :style="{ backgroundColor: getAgtBackgroundColor(a.agt) }"
                  >
                    <div class="agt-chip-label">AGT {{ a.agt }}</div>
                    <div class="agt-chip-acres">
                      {{ a.acreage | prettyInteger }} ac
                    </div>
                  </div>
                </div>
                <div class="body-2 mt-2">
                  Displaying AGT soil cluster yield for
                  {{ crop.fieldCountWithAgt }} out of {{ crop.totalHybridFieldCount }}
                  {{ (crop.cropName || '').toLowerCase() }} fields. AGT yield requires machine-source yield data and is calculated
                  at 0.25 ac resolution.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 pl-5 pr-5">
        <div class="section-title mb-1">Carbon Intensity Estimates</div>
        <div v-if="!hasCiData" class="body-2">
          No carbon intensity data available. Harvest data for corn, soybeans, or rice is required.
        </div>
        <div v-else>

          <div v-if="hasIneligibleHybridCrops" class="body-2 section-text mb-2">
            Carbon Intensity (CI) is presently only calculated for corn, soybeans, and rice.
          </div>
          <div
            v-for="crop in ciByCrop"
            :key="crop.cropId"
            class="mx-6 mb-4 avoid-break"
          >
            <div class="subtitle-1 font-weight-bold mb-1">
              {{ crop.cropName }} ({{ crop.unit }})
            </div>
            <div class="row hybrid-chart-row">
              <div class="col-9">
                <apexchart
                  :options="ciChartOptionsForCrop(crop)"
                  :series="ciChartSeriesForCrop(crop)"
                  type="boxPlot"
                  :height="ciChartHeight(crop)"
                />
              </div>
              <div class="col-3">
                <table class="ci-stats-table mr-2">
                  <tbody>
                    <tr v-for="r in ciStatRowsForCrop(crop)" :key="r.label">
                      <td class="stat-name">{{ r.label }}</td>
                      <td class="stat-value">{{ formatCiStatValue(r.value) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="body-2 pl-5">
            Estimates from R&D GREET 2023 without ΔSOC.
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 pl-5 pr-5">
        <div class="section-title mb-1">Carbon Program Payments in {{ reportData.year }}</div>
        <div v-if="!hasContractPaymentData" class="body-2">
          No carbon program payment data available for {{ reportData.year }}. Data completion, program enrollment, and contract execution are required.
        </div>
        <div class="avoid-break" v-else>
          <!-- Crop Payment Summary -->
          <div class="subtitle-1 font-weight-bold mb-2">Payment Summary by Crop</div>
          <div class="row mb-4">
            <div v-for="crop in cropPaymentSummary" :key="crop.cropId" class="col-3">
              <div class="payment-summary-card">
                <div class="payment-crop-name">{{ crop.cropName }}</div>
                <div class="d-flex flex-column justify-space-between flex-grow-1">
                  <div class="payment-metric mb-1">
                    <span class="payment-value">${{ crop.totalPayment | prettyInteger }}</span>
                    <span class="payment-label">Total Payment</span>
                  </div>
                  <div class="payment-metric mb-1">
                    <span class="payment-value">{{ crop.totalCo2e.toFixed(1) }}</span>
                    <span class="payment-label">tCO₂e reduction, est.</span>
                  </div>
                  <div class="payment-metric mb-1">
                    <span class="payment-value">{{ crop.co2ePerAcre.toFixed(2) }}</span>
                    <span class="payment-label">tCO₂e/acre</span>
                  </div>
                  <div class="payment-metric">
                    <span class="payment-value">{{ crop.acres | prettyInteger }}</span>
                    <span class="payment-label">Acres</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Practice Adoption by Crop -->
          <div class="subtitle-1 font-weight-bold mb-2">Program Regenerative Practices by Crop</div>
          <div v-for="crop in practiceAdoptionByCrop" :key="crop.cropId" class="mb-4 avoid-break">
            <div class="subtitle-2 font-weight-medium mb-2">{{ crop.cropName }} ({{ crop.totalAcres | prettyInteger }} acres)</div>
            <div class="d-flex flex-wrap">
              <div v-for="practice in crop.practices" :key="practice.name" class="practice-chip ma-1">
                <div class="practice-name mb-1">{{ practice.name }}</div>
                <div class="d-flex align-center">
                  <span class="practice-acres mr-1">{{ practice.acres | prettyInteger }} ac</span>
                  <span class="practice-percent">({{ practice.percentage }}%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 pl-5 pr-5">
        <div class="section-title mb-1">Carbon Opportunity</div>
        <div v-if="carbonOpportunityCrops.length && topClimateZone" class="body-2 section-text mb-2">
          Practice combinations in your climate region ({{ topClimateZone }})
        </div>
        <div v-else-if="carbonOpportunityCrops.length && !topClimateZone" class="body-2 section-text mb-2">
          Couldn't detect primary climate zone: displaying carbon opportunity for warm-moist climate zone.
        </div>
        <div v-if="!carbonOpportunityCrops.length" class="section-text body-2">
          No carbon opportunity data available.
        </div>
        <div v-else>
          <div
            v-for="crop in carbonOpportunityCrops"
            :key="crop.cropKey"
            class="mb-5 avoid-break"
          >
            <div class="subtitle-1 font-weight-bold mb-2">
              {{ crop.cropTitle }}
            </div>
            <div class="row">
              <!-- Group combinations -->
              <div class="col-6">
                <div class="carbon-subtitle mb-2">
                  Most popular combinations
                </div>
                <div v-for="rec in crop.groups" :key="rec.practice" class="carbon-row mb-1">
                  <div class="d-flex align-center">
                    <div class="carbon-practice flex-grow-1">{{ rec.practice }} </div>
                    <div class="carbon-label">
                      {{ labelForPercent(rec.percent) }}
                    </div>
                  </div>
                  <meter
                    class="carbon-meter"
                    :value="Math.min(rec.percent,100)"
                    max="100"
                  ></meter>
                </div>
              </div>
              <!-- Individual practices -->
              <div class="col-6">
                <div class="carbon-subtitle mb-2">
                  Individual practices
                </div>
                <div v-for="rec in crop.individuals" :key="rec.practice" class="carbon-row mb-1">
                  <div class="d-flex align-center">
                    <div class="carbon-practice flex-grow-1">{{ rec.practice }}</div>
                    <div class="carbon-label">
                      {{ labelForPercent(rec.percent) }}
                    </div>
                  </div>
                  <meter
                    class="carbon-meter"
                    :value="Math.min(rec.percent,100)"
                    max="100"
                  ></meter>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12 pl-5 pr-5">
        <div class="section-title mb-1">Top Reported Hybrids in Organization <i>{{ reportData.corporation_name }}</i></div>
        <div v-if="!hasOrgHybridData" class="body-2">
          No organization-wide hybrid yield data available.
        </div>
        <div v-else>
          <div class="body-2">
            Showing top yielding hybrids per crop. Only reported hybrids planted on at least 5 fields are included.
          </div>
          <div
            v-for="crop in corpTopHybridsByCrop"
            :key="crop.cropName"
            class="mx-5 avoid-break"
          >
            <div class="subtitle-1 font-weight-bold mb-1">
              {{ crop.cropName }}<span v-if="crop.unit"> ({{ crop.unit }})</span>
            </div>
            <div v-if="crop.unitError" class="body-2 text--error">
              {{ crop.unitError }}
            </div>
            <div v-else-if="crop.hybrids.length === 0" class="body-2">
              No reported hybrid yield data available for {{ crop.cropName }}.
            </div>
            <div v-else class="row hybrid-chart-row">
              <div class="col-7">
                <div class="axis-label ml-1">Hybrid name</div>
                <apexchart
                  :options="hybridChartOptionsForCrop(crop)"
                  :series="corpHybridChartSeriesForCrop(crop)"
                  type="boxPlot"
                  :height="hybridChartHeight(crop)"
                />
              </div>
              <div class="col-5">
                <table class="hybrid-stats-table">
                  <thead>
                    <tr>
                      <th>Hybrid</th>
                      <th>Fields</th>
                      <th>Acres</th>
                      <th>Avg</th>
                      <th>Median</th>
                      <th>Q1</th>
                      <th>Q3</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="h in crop.hybrids" :key="h.hybridName">
                      <td class="h-name">{{ h.hybridName }}</td>
                      <td>{{ h.fieldCount }}</td>
                      <td>{{ h.acreage | prettyInteger }} ac</td>
                      <td>{{ h.stats.avg.toFixed(0) }}</td>
                      <td>{{ h.stats.median.toFixed(0) }}</td>
                      <td>{{ h.stats.q1.toFixed(0) }}</td>
                      <td>{{ h.stats.q3.toFixed(0) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { GeoJsonLayer } from "@deck.gl/layers"
import { MapboxLayer } from "@deck.gl/mapbox"
import mapboxgl from "mapbox-gl"
import { getThemeVariable, hexToRgbList, arrayAverage, standardDeviation, getAgtColor } from "@/utility"
import SoilTextureTriangle from "@/components/reports/SoilTextureTriangle"

import { MAPBOX_TOKEN } from "@/constants/map"
import { CROP_DISPLAY_NAME } from "@/constants"

export default {
  name: "GrowerReport",
  props: ["reportData"],
  components: {
    SoilTextureTriangle
  },
  data() {
    return {
      carbonOpportunityThresholds: [
        { max: 15, label: 'Low' },
        { max: 40, label: 'Medium' },
        { max: 85, label: 'High' },
        { max: 100, label: 'Near max' },
        { max: Infinity, label: 'Max' }
      ],
      nullSeedVarieties: ["Unknown", "---", "-", "NA"]
    }
  },

  computed: {
    farmDisplayName() {
      return this.reportData.farm_name || this.reportData.client_name
    },
    acreageByRegions() {
      return this.reportData.acreage_by_regions || []
    },

    hasHybridData() {
      return Object.keys(this.reportData.hybrid_yield_by_field || {}).length > 0
    },

    totalFieldCount() {
      return Object.keys(this.reportData.field_metadata || {}).length
    },

    fieldsWithHarvestCount() {
      return Object.keys(this.reportData.hybrid_yield_by_field || {}).length
    },

    acreageByCrop() {
      if (!this.hasHybridData) return []

      const cropData = {}

      // Group fields by crop
      Object.entries(this.reportData.hybrid_yield_by_field).forEach(([fieldId, harvestData]) => {
        const cropId = harvestData.crop_id
        const fieldMetadata = this.reportData.field_metadata[fieldId]

        if (!fieldMetadata) return

        if (!cropData[cropId]) {
          cropData[cropId] = {
            cropId,
            cropName: CROP_DISPLAY_NAME[parseInt(cropId)] || `Crop ${cropId}`,
            fields: [],
            totalAcreage: 0
          }
        }

        cropData[cropId].fields.push({
          fieldId,
          acreage: fieldMetadata.acreage,
          geoid_id: fieldMetadata.geoid_id,
          county_name: fieldMetadata.county_name,
          state_name: fieldMetadata.state_name,
          country_name: fieldMetadata.country_name
        })

        cropData[cropId].totalAcreage += fieldMetadata.acreage
      })

      // Calculate acreage by region for each crop
      return Object.values(cropData).map(crop => {
        const regionAcreage = {}

        crop.fields.forEach(field => {
          const regionKey = field.geoid_id
          if (!regionAcreage[regionKey]) {
            regionAcreage[regionKey] = {
              geoid_id: field.geoid_id,
              county_name: field.county_name,
              state_name: field.state_name,
              country_name: field.country_name,
              acreage: 0
            }
          }
          regionAcreage[regionKey].acreage += field.acreage
        })

        return {
          ...crop,
          cropFieldCount: crop.fields.length,
          regions: Object.values(regionAcreage)
        }
      })
    },

    plantedAcreage() {
      return this.acreageByCrop.reduce((total, crop) => total + crop.totalAcreage, 0)
    },

    acresWithHarvest() {
      return this.acreageByCrop.reduce((total, crop) => total + crop.totalAcreage, 0)
    },

    harvestDataStats() {
      if (!this.hasHybridData) return { manual: 0, machine: 0, total: 0 }

      const stats = Object.values(this.reportData.hybrid_yield_by_field).reduce((acc, harvestData) => {
        acc.total++
        if (harvestData.yield_source === "User Input") {
          acc.manual++
        } else if (harvestData.yield_source === "shapefile") {
          acc.machine++
        }
        return acc
      }, { manual: 0, machine: 0, total: 0 })

      return stats
    },

    manualDataPercentage() {
      const stats = this.harvestDataStats
      return stats.total > 0 ? Math.round((stats.manual / stats.total) * 100) : 0
    },

    machineDataPercentage() {
      const stats = this.harvestDataStats
      return stats.total > 0 ? Math.round((stats.machine / stats.total) * 100) : 0
    },
    harvestMatrix() {
      if (!this.hasHybridData) return { regions: [], crops: [], acreage: {}, fieldCounts: {} }

      const crops = this.acreageByCrop.map(c => ({
        cropId: c.cropId,
        cropName: c.cropName,
        totalAcreage: c.totalAcreage,
        cropFieldCount: c.cropFieldCount
      }))

      const regionIds = new Set()
      const acreage = {}
      const fieldCounts = {}

      this.acreageByCrop.forEach(crop => {
        crop.regions.forEach(region => {
          regionIds.add(region.geoid_id)

          if (!acreage[region.geoid_id]) acreage[region.geoid_id] = {}
          if (!fieldCounts[region.geoid_id]) fieldCounts[region.geoid_id] = {}

          acreage[region.geoid_id][crop.cropId] = region.acreage

          const fieldsInRegion = crop.fields.filter(field => field.geoid_id === region.geoid_id)
          fieldCounts[region.geoid_id][crop.cropId] = fieldsInRegion.length
        })
      })

      // Preserve ordering from acreageByRegions (left table)
      const regions = this.acreageByRegions
        .filter(r => regionIds.has(r.geoid_id))
        .map(r => ({
          geoid_id: r.geoid_id,
          county_name: r.county_name,
          state_name: r.state_name,
          country_name: r.country_name
        }))

      return { regions, crops, acreage, fieldCounts }
    },
    yieldsByCrop() {
      if (!this.hasHybridData) return []
      const hybridFieldData = this.reportData.hybrid_yield_by_field || {}
      const hybridsByCropRaw = this.aggregateHybridData(hybridFieldData)
      const result = []
      for (const cropObj of Object.values(hybridsByCropRaw)) {
        if (cropObj.unitsSet.size > 1) {
          result.push({
            cropId: cropObj.cropId,
            cropName: cropObj.cropName,
            unitError: "Cannot display: inconsistent yield units for this crop."
          })
          continue
        }
        const unit = [...cropObj.unitsSet][0] || ""
        const hybrids = []
        for (const [hybridName, data] of Object.entries(cropObj.hybridAggByName)) {
          const yields = data.yields
          if (!yields.length) continue
          const mean = arrayAverage(yields)
          const std = yields.length > 1 ? standardDeviation(yields) : 0
          const stats = this.computeBoxPlotStats(yields)
          hybrids.push({ hybridName, mean, std, yields, stats, acreage: data.acreage })
        }
        hybrids.sort((a, b) => b.mean - a.mean)
        result.push({ cropId: cropObj.cropId, cropName: cropObj.cropName, unit, hybrids })
      }
      return result.sort((a, b) => a.cropName.localeCompare(b.cropName))
    },
    agtYieldsByCrop() {
      if (Object.keys(this.reportData.agt_voxel_yield_by_field_and_crop || {}).length === 0) {
        return []
      }
      const agt_voxel_yield = this.reportData.agt_voxel_yield_by_field_and_crop || {}
      const hybridFields = this.reportData.hybrid_yield_by_field || {}

      // Build crop -> unit set (from hybrid yield) & field list
      const cropUnits = {}
      const cropHybridFieldCounts = {}
      for (const [, rec] of Object.entries(hybridFields)) {
        const cropId = parseInt(rec.crop_id)
        cropUnits[cropId] = cropUnits[cropId] || new Set()
        if (rec.units) cropUnits[cropId].add(rec.units.trim())
        cropHybridFieldCounts[cropId] = (cropHybridFieldCounts[cropId] || 0) + 1
      }

      const crops = {}
      for (const [fieldId, cropMap] of Object.entries(agt_voxel_yield)) {
        for (const [cropIdStr, agtArr] of Object.entries(cropMap || {})) {
          if (agtArr == null || agtArr.length == 0) {
            continue
          }
          const cropId = parseInt(cropIdStr)
          if (!crops[cropId]) {
            const unitsSet = cropUnits[cropId] || new Set()
            crops[cropId] = {
              cropId,
              cropName: CROP_DISPLAY_NAME[parseInt(cropId)] || `Crop ${cropId}`,
              unitsSet,
              agtMap: {},
              fieldsWithAgt: new Set()
            }
          }
          const cropObj = crops[cropId]
          cropObj.fieldsWithAgt.add(fieldId)
          agtArr.forEach(entry => {
            const agt = entry.agt
            if (!cropObj.agtMap[agt]) {
              cropObj.agtMap[agt] = { agt, yields: [], acreage: 0 }
            }
            const y = parseFloat(entry.yield_per_agt)
            if (!isNaN(y)) cropObj.agtMap[agt].yields.push(y)
            cropObj.agtMap[agt].acreage += entry.acres || 0
          })
        }
      }

      const result = []
      for (const cropObj of Object.values(crops)) {
        if (cropObj.unitsSet.size > 1) {
          result.push({
            cropId: cropObj.cropId,
            cropName: cropObj.cropName,
            unitError: "Cannot display: inconsistent yield units for this crop.",
            agts: [],
            fieldCountWithAgt: cropObj.fieldsWithAgt.size,
            totalHybridFieldCount: cropHybridFieldCounts[cropObj.cropId] || 0
          })
          continue
        }
        const unit = [...cropObj.unitsSet][0] || ""
        const agts = Object.values(cropObj.agtMap)
          .map(a => {
            const stats = this.computeBoxPlotStats(a.yields)
            return { ...a, stats }
          })
          .filter(a => a.stats)
          .filter(a => a.acreage > 1)
          .sort((a, b) => b.acreage - a.acreage)
          .slice(0, 6) // top 6 by acreage
        result.push({
          cropId: cropObj.cropId,
          cropName: cropObj.cropName,
          unit,
          agts,
          fieldCountWithAgt: cropObj.fieldsWithAgt.size,
          totalHybridFieldCount: cropHybridFieldCounts[cropObj.cropId] || 0
        })
      }
      return result.sort((a, b) => a.cropName.localeCompare(b.cropName))
    },
    hasCiData() {
      const elig = [4,5,6]
      const ci = this.reportData.ci_by_field || {}
      for (const fieldId in ci) {
        const crops = ci[fieldId]
        for (const cropId of Object.keys(crops || {})) {
          if (elig.includes(parseInt(cropId))) return true
        }
      }
      return false
    },
    hasIneligibleHybridCrops() {
      if (!this.hasHybridData) return false
      const elig = new Set([4,5,6])
      return this.yieldsByCrop.some(c => !elig.has(parseInt(c.cropId)))
    },
    ciByCrop() {
      if (!this.hasCiData) return []
      const ciByField = this.reportData.ci_by_field || {}
      const fieldMeta = this.reportData.field_metadata || {}
      const crops = {}
      for (const [fieldId, cropMap] of Object.entries(ciByField)) {
        for (const [cropIdStr, ciObj] of Object.entries(cropMap)) {
          const cropId = parseInt(cropIdStr)
          if (![4,5,6].includes(cropId)) continue
          if (!crops[cropId]) {
            crops[cropId] = {
              cropId,
              values: [],
              cropName: CROP_DISPLAY_NAME[parseInt(cropId)] || `Crop ${cropId}`,
              unit: ciObj.ci_unit || '',
              fields: []
            }
          }
          const value = (typeof ciObj.combined_converted_score === 'number'
            ? ciObj.combined_converted_score
            : ciObj.ci_score)
          const fm = fieldMeta[fieldId] || {}
          crops[cropId].fields.push({
            fieldId,
            fieldName: fm.name || `Field ${fieldId}`,
            ci_value: value
          })
          if (typeof value === 'number' && !isNaN(value)) crops[cropId].values.push(value)
        }
      }
      // finalize aggregated stats + single item for chart
      return Object.values(crops).map(c => {
        const stats = this.computeBoxPlotStats(c.values)
        return {
          ...c,
          aggregatedStats: { ...stats, unit: c.unit },
          items: stats ? [{ label: "All Fields", stats }] : []
        }
      }).sort((a,b) => a.cropName.localeCompare(b.cropName))
    },
    hasContractPaymentData() {
      return Array.isArray(this.reportData.contract_payment_table)
        && this.reportData.contract_payment_table.length > 0
    },

    cropPaymentSummary() {
      if (!this.hasContractPaymentData) return []

      const cropData = {}

      this.reportData.contract_payment_table.forEach(record => {
        const cropId = record.crop_id
        const cropName = CROP_DISPLAY_NAME[parseInt(cropId)] || record.crop || `Crop ${cropId}`

        if (!cropData[cropId]) {
          cropData[cropId] = {
            cropId,
            cropName,
            totalPayment: 0,
            totalCo2e: 0,
            acres: 0
          }
        }

        // Parse payment string (remove commas and convert to number)
        const paymentStr = record.total_payment.toString().replace(/[,$]/g, '')
        const payment = parseFloat(paymentStr) || 0

        cropData[cropId].totalPayment += payment
        cropData[cropId].totalCo2e += parseFloat(record.total_co2e) || 0
        cropData[cropId].acres += parseFloat(record.acres) || 0
      })

      return Object.values(cropData).map(crop => ({
        ...crop,
        co2ePerAcre: crop.acres > 0 ? crop.totalCo2e / crop.acres : 0
      })).sort((a, b) => a.cropName.localeCompare(b.cropName))
    },

    practiceAdoptionByCrop() {
      if (!this.hasContractPaymentData) return []

      const cropData = {}

      this.reportData.contract_payment_table.forEach(record => {
        const cropId = record.crop_id
        const cropName = CROP_DISPLAY_NAME[parseInt(cropId)] || record.crop || `Crop ${cropId}`
        const acres = parseFloat(record.acres) || 0

        if (!cropData[cropId]) {
          cropData[cropId] = {
            cropId,
            cropName,
            totalAcres: 0,
            practiceAcres: {}
          }
        }

        cropData[cropId].totalAcres += acres

        // Split practices string and add acres for each practice
        if (record.practices) {
          const practices = record.practices.split(',').map(p => p.trim())
          practices.forEach(practice => {
            if (!cropData[cropId].practiceAcres[practice]) {
              cropData[cropId].practiceAcres[practice] = 0
            }
            cropData[cropId].practiceAcres[practice] += acres
          })
        }
      })

      // Convert to final format with percentages
      return Object.values(cropData).map(crop => {
        const practices = Object.entries(crop.practiceAcres).map(([name, acres]) => ({
          name,
          acres,
          percentage: crop.totalAcres > 0 ? Math.round((acres / crop.totalAcres) * 100) : 0
        })).sort((a, b) => b.acres - a.acres) // Sort by acres descending

        return {
          cropId: crop.cropId,
          cropName: crop.cropName,
          totalAcres: crop.totalAcres,
          practices
        }
      }).sort((a, b) => a.cropName.localeCompare(b.cropName))
    },
    carbonOpportunityRaw() {
      return this.reportData.carbon_opportunity || {}
    },
    carbonOpportunityNameMap() {
      return {
        soybeans: "Soybeans/Alfalfa/N-Fixing",
        wheat: "Grains (Wheat/Durum/Rye/Barley/Oats)"
      }
    },
    carbonOpportunityCrops() {
      const out = []
      for (const [cropKey, arr] of Object.entries(this.carbonOpportunityRaw)) {
        if (!Array.isArray(arr) || !arr.length) continue
        const groups = arr.filter(r => r.type === 'group').map(r => ({
          ...r,
          percent: this.parsePercent(r.percent_of_max_cap)
        }))
        const individuals = arr.filter(r => r.type === 'individual').map(r => ({
          ...r,
          percent: this.parsePercent(r.percent_of_max_cap)
        }))
        out.push({
          cropKey,
          cropTitle: this.carbonOpportunityNameMap[cropKey] || (cropKey.charAt(0).toUpperCase() + cropKey.slice(1)),
          groups,
          individuals
        })
      }
      return out
    },
    topClimateZone() {
      if (this.reportData.primary_climate_zone) return this.reportData.primary_climate_zone
      return null
    },

    soilData() {
      const soilData = this.reportData.soil_data_by_field || {}
      return {
        growerStats: soilData.grower_soil_stats || {},
        fieldTextures: soilData.field_texture_avg || {}
      }
    },

    soilTrianglePoints() {
      const fieldTextures = this.soilData.fieldTextures
      const points = []

      // Convert field texture data to triangle points
      Object.entries(fieldTextures).forEach(([, texture]) => {
        points.push({
          sand: texture.sand_pct_avg || null,
          silt: texture.silt_pct_avg || null,
          clay: texture.clay_pct_avg || null,
          label: null, // don't bother showing field names
        })
      })

      return points
    },

    soilStatsSource() {
      const cecStats = this.soilData.growerStats.cec
      return cecStats?.source || 'Unknown'
    },

    hasOrgHybridData() {
      return Object.keys(this.reportData.hybrid_yield_by_org_fields || {}).length > 0
    },

    corpTopHybridsByCrop() {
      const orgHybridMap = this.reportData.hybrid_yield_by_org_fields || {}
      if (!Object.keys(orgHybridMap).length) return []

      const hybridsByCropRaw = this.aggregateHybridData(orgHybridMap)

      const results = []
      for (const cropAgg of Object.values(hybridsByCropRaw)) {
        if (cropAgg.unitsSet.size > 1) {
          results.push({ cropId: cropAgg.cropId, cropName: cropAgg.cropName, unitError: 'Cannot display: inconsistent yield units for this crop.', hybrids: [] })
          continue
        }
        const unit = [...cropAgg.unitsSet][0] || ''
        const hybridAggs = Object.entries(cropAgg.hybridAggByName).map(([hybridName, data]) => ({ hybridName, ...data }))

        const hybrids = []
        for (const h of hybridAggs) {
          if (!h.yields.length) continue
          // could do an acreage check here, or even a farm count (farm count not implemented)
          if ((h.fieldCount || 0) < 5) continue
          const stats = this.computeBoxPlotStats(h.yields)
          hybrids.push({ hybridName: h.hybridName, fieldCount: h.fieldCount, acreage: h.acreage, stats })
        }
        hybrids.sort((a, b) => (b.stats.avg - a.stats.avg))
        const top = hybrids.slice(0, 8)
        if (top.length === 0) {
          // avoid blank box plots
          continue
        }

        results.push({ cropId: cropAgg.cropId, cropName: cropAgg.cropName, unit, hybrids: top })
      }

      return results.sort((a, b) => a.cropName.localeCompare(b.cropName))
    },
    agtSoilBreakdown() {
      const stats = ((this.reportData.soil_data_by_field || {}).agt_soil_stats) || {}
      const arr = Object.entries(stats).map(([agtId, rec]) => {
        return {
          agt: agtId,
          shortAgt: this.cleanAgtId(agtId),
          acreage: rec.acreage || 0,
          cecAvg: (rec.cec && typeof rec.cec.average === 'number') ? rec.cec.average : null,
          omAvg: (rec.soc && typeof rec.soc.average === 'number') ? rec.soc.average : (rec.om && typeof rec.om.average === 'number' ? rec.om.average : null)
        }
      }).filter(r => r.acreage > 0)
      arr.sort((a,b) => b.acreage - a.acreage)
      return arr
    }
  },
  methods: {
    fitBoundsRotated(bounds, options, eventData, map) {
      options = Object.assign(
        {
          padding: {
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
          },
          offset: [0, 0],
          maxZoom: map.transform.maxZoom,
        },
        options
      )

      if (typeof options.padding === "number") {
        const p = options.padding
        options.padding = {
          top: p,
          bottom: p,
          right: p,
          left: p,
        }
      }

      bounds = mapboxgl.LngLatBounds.convert(bounds)

      // paddingOffset affects the map's center, used in the options for
      // `easeTo` and `flyTo` in subsequent calls
      const paddingOffset = [
          options.padding.left - options.padding.right,
          options.padding.top - options.padding.bottom,
        ],
        lateralPadding = Math.min(options.padding.right, options.padding.left),
        verticalPadding = Math.min(options.padding.top, options.padding.bottom)

      options.offset = [
        options.offset[0] + paddingOffset[0],
        options.offset[1] + paddingOffset[1],
      ]
      options.bearing = options.bearing || this.getBearing()

      const offset = mapboxgl.Point.convert(options.offset),
        tr = map.transform,
        nw = tr.project(bounds.getNorthWest()),
        se = tr.project(bounds.getSouthEast()),
        size = se.sub(nw)

      // START CUSTOM ROTATION
      // "cropped rectangle rotation method"
      // https://stackoverflow.com/questions/33866535/how-to-scale-a-rotated-rectangle-to-always-fit-another-rectangle
      const theta = options.bearing * (Math.PI / 180),
        W
          = size.x * Math.abs(Math.cos(theta))
            + size.y * Math.abs(Math.sin(theta)),
        H
          = size.x * Math.abs(Math.sin(theta))
            + size.y * Math.abs(Math.cos(theta)),
        rotatedSize = { x: W, y: H },
        // END CUSTOM ROTATION
        scaleX
          = (tr.width - lateralPadding * 2 - Math.abs(offset.x) * 2)
            / rotatedSize.x,
        scaleY
          = (tr.height - verticalPadding * 2 - Math.abs(offset.y) * 2)
            / rotatedSize.y

      if (scaleY < 0 || scaleX < 0) {
        if (typeof console !== "undefined")
          console.warn(
            "Map cannot fit within canvas with the given bounds, padding, and/or offset."
          )
        return map
      }

      options.center = tr.unproject(nw.add(se).div(2))
      options.zoom = Math.min(
        tr.scaleZoom(tr.scale * Math.min(scaleX, scaleY)),
        options.maxZoom
      )

      return options.linear
        ? map.easeTo(options, eventData)
        : map.flyTo(options, eventData)
    },
    getAcreageForRegionCrop(regionId, cropId) {
      const cell = this.harvestMatrix.acreage[regionId]
      return cell && cell[cropId] ? cell[cropId] : 0
    },

    getFieldCountForRegionCrop(regionId, cropId) {
      const cell = this.harvestMatrix.fieldCounts[regionId]
      return cell && cell[cropId] ? cell[cropId] : 0
    },

    parseSeedVariety(val) {
      if (val == null) return "Unknown"
      const s = String(val).trim()
      if (s.startsWith("[") && s.endsWith("]")) {
        try {
          const parsed = JSON.parse(s)
        ; if (Array.isArray(parsed) && parsed.length) return parsed[0]
        } catch { return "Unknown" }
      }
      return s
    },
    computeBoxPlotStats(values) {
      if (!values.length) return null
      const sorted = [...values].sort((a, b) => a - b)
      const min = sorted[0]
      const max = sorted[sorted.length - 1]
      const median = this.quantile(sorted, 0.5)
      const q1 = this.quantile(sorted, 0.25)
      const q3 = this.quantile(sorted, 0.75)
      const avg = arrayAverage(sorted)
      return { min, q1, median, q3, max, avg }
    },
    quantile(sortedArr, q) {
      if (!sortedArr.length) return 0
      const pos = (sortedArr.length - 1) * q
      const base = Math.floor(pos)
      const rest = pos - base
      if (sortedArr[base + 1] !== undefined) {
        return sortedArr[base] + rest * (sortedArr[base + 1] - sortedArr[base])
      }
      return sortedArr[base]
    },
    // Generic reusable chart options builder
    buildBoxPlotChartOptions({ categories, unit, upperColor, lowerColor, avgColor }) {
      return {
        chart: { type: "boxPlot", toolbar: { show: false }, offsetY: -25 },
        plotOptions: {
          bar: { horizontal: true, barHeight: "40%" },
          boxPlot: {
            barHeight: "40%",
            colors: { upper: upperColor, lower: lowerColor }
          }
        },
        colors: [avgColor, upperColor],
        dataLabels: { enabled: false },
        yaxis: {
          categories,
          labels: { style: { fontSize: "13px", fontWeight: 600 } }
        },
        xaxis: {
          title: { text: unit || "" },
          labels: { formatter: v => (v == null ? "" : Number(v).toFixed(0)) }
        },
        tooltip: { enabled: false },
        legend: { show: false }
      }
    },
    // Generic series builder (expects items: [{label, stats:{min,q1,median,q3,max,avg}}])
    buildBoxPlotSeries(items) {
      const boxData = []
      const avgBars = []
      for (const it of items) {
        const s = it.stats
        boxData.push({ x: it.label, y: [s.min, s.q1, s.median, s.q3, s.max] })
        avgBars.push({ x: it.label, y: s.avg })
      }

      return [
        { name: "Average", type: "bar", data: avgBars },
        { name: "Distribution", type: "boxPlot", data: boxData }
      ]
    },

    // Updated hybrid chart methods to reuse generic builders
    hybridChartOptionsForCrop(crop) {
      if (crop.unitError) return {}
      return this.buildBoxPlotChartOptions({
        categories: crop.hybrids.map(h => h.hybridName),
        unit: crop.unit,
        upperColor: "var(--arva-dark-green)",
        lowerColor: "var(--arva-light-green)",
        avgColor: "rgba(0, 90, 40, 0.15)"
      })
    },
    hybridChartSeriesForCrop(crop) {
      if (crop.unitError) return []
      const items = crop.hybrids.map(h => ({ label: h.hybridName, stats: h.stats }))
      return this.buildBoxPlotSeries(items)
    },
    hybridChartHeight(crop) {
      const len = (crop.hybrids && crop.hybrids.length) ? crop.hybrids.length : 1
      if (len == 1) {
        return 130
      } else if (len == 2) {
        return 175
      } else {
        return 60 * len
      }
    },
    corpHybridChartSeriesForCrop(crop) {
      if (crop.unitError) return []
      const items = crop.hybrids.map(h => ({ label: h.hybridName, stats: h.stats }))
      return this.buildBoxPlotSeries(items)
    },

    // CI chart methods
    ciChartOptionsForCrop(crop) {
      return this.buildBoxPlotChartOptions({
        categories: crop.items.map(i => i.label),
        unit: crop.unit,
        upperColor: "var(--arva-brown)",
        lowerColor: "var(--arva-peach)",
        avgColor: "rgba(139,69,19,0.15)"
      })
    },
    ciChartSeriesForCrop(crop) {
      return this.buildBoxPlotSeries(crop.items)
    },
    ciChartHeight(_crop) {
      return 140
    },
    ciStatRowsForCrop(crop) {
      const s = crop.aggregatedStats
      if (!s) return []
      return [
        { label: 'Average', value: s.avg != null ? `${Math.round(s.avg)} ${s.unit}` : '-' },
        { label: 'Median', value: s.median != null ? `${Math.round(s.median)} ${s.unit}` : '-' },
        { label: 'Q1', value: s.q1 != null ? `${Math.round(s.q1)} ${s.unit}` : '-' },
        { label: 'Q3', value: s.q3 != null ? `${Math.round(s.q3)} ${s.unit}` : '-' },
        { label: 'Min', value: s.min != null ? `${Math.round(s.min)} ${s.unit}` : '-' },
        { label: 'Max', value: s.max != null ? `${Math.round(s.max)} ${s.unit}` : '-' },
        { label: 'Field Count', value: crop.fields.length }
      ]
    },
    formatCiStatValue(v) {
      if (v == null || v === '') return '-'
      if (typeof v === 'number') return v.toFixed(0)
      return v
    },

    agtChartOptionsForCrop(crop) {
      if (crop.unitError) return {}
      return this.buildBoxPlotChartOptions({
        categories: crop.agts.map(a => String(a.agt)),
        unit: crop.unit,
        upperColor: "var(--arva-dark-green)",
        lowerColor: "var(--arva-light-green)",
        avgColor: "rgba(0, 90, 40, 0.15)"
      })
    },
    agtChartSeriesForCrop(crop) {
      if (crop.unitError) return []
      const items = crop.agts.map(a => ({ label: String(a.agt), stats: a.stats }))
      return this.buildBoxPlotSeries(items)
    },
    agtChartHeight(crop) {
      const len = (crop.agts && crop.agts.length) ? crop.agts.length : 1
      return Math.max(60 * len, 130)
    },
    getAgtBackgroundColor(agt) {
      // try to darken to not clash with other colors so much
      const hexColor = getAgtColor(agt);
      const rgbList = hexToRgbList(hexColor);
      for (let i = 0; i < rgbList.length; i++) {
        rgbList[i] = rgbList[i] * 0.8;
      }
      const rgbString = `rgb(${rgbList.join(",")})`;
      return rgbString;
    },
    parsePercent(str) {
      if (!str) return 0
      const n = parseFloat(String(str).replace('%',''))
      return isNaN(n) ? 0 : n
    },
    labelForPercent(p) {
      for (const t of this.carbonOpportunityThresholds) {
        if (p < t.max) return t.label
      }
      return 'N/A'
    },

    formatSoilStat(statObj, field) {
      if (!statObj || statObj[field] == null) return '—'
      const value = statObj[field]
      return typeof value === 'number' ? value.toFixed(1) : value
    },
    aggregateHybridData(data) {
      const hybridsByCropRaw = {}
      for (const [, rec] of Object.entries(data)) {
        const cropId = parseInt(rec.crop_id)
        let cropName = CROP_DISPLAY_NAME[cropId] || `Crop ${cropId}`
        if (cropName == 'Corn' && rec.harvest_type && rec.harvest_type.toLowerCase() == 'silage') {
          cropName = 'Corn (Silage)'
        }
        if (!hybridsByCropRaw[cropName]) {
          hybridsByCropRaw[cropName] = {
            cropId,
            cropName,
            unitsSet: new Set(),
            hybridAggByName: {}
          }
        }
        const cropObj = hybridsByCropRaw[cropName]
        const unit = (rec.units || "").trim()
        if (unit) cropObj.unitsSet.add(unit)
        const hybridName = this.parseSeedVariety(rec.seed_variety)

        if (this.nullSeedVarieties.includes(hybridName)) {
          continue
        }

        if (!cropObj.hybridAggByName[hybridName]) {
          cropObj.hybridAggByName[hybridName] = { yields: [], acreage: 0, fieldCount: 0 }
        }
        const hybridAgg = cropObj.hybridAggByName[hybridName]
        const y = parseFloat(rec.yield)
        if (!isNaN(y)) hybridAgg.yields.push(y)
        const acreage = parseFloat(rec.acreage) || 0
        hybridAgg.acreage += acreage
        hybridAgg.fieldCount += 1
      }
      return hybridsByCropRaw
    },
    formatNumber(v, d = 1) {
      return (typeof v === 'number') ? v.toFixed(d) : '—'
    },
    cleanAgtId(id) {
      if (id == null) return '—'
      const n = parseFloat(id)
      if (isNaN(n)) return String(id)
      return n % 1 === 0 ? n.toString() : n.toFixed(0)
    }
  },

  mounted() {
    mapboxgl.accessToken = MAPBOX_TOKEN
    const map = new mapboxgl.Map({
      preserveDrawingBuffer: true,
      container: "mapbox-map",
      height: "100%",
      width: "100%",
      style: "mapbox://styles/mapbox/satellite-v9",
      center: [-91.6, 34.4],
      zoom: 9,
      pitch: 45,
      attributionControl: false
    })

    const fieldBoundaries = this.reportData.field_bounds
    const fieldBoundariesLayer = new MapboxLayer({
      id: "fieldBoundariesLayer",
      type: GeoJsonLayer,
      data: fieldBoundaries,
      stroked: true,
      filled: false,
      extruded: false,
      getLineColor: () => hexToRgbList(getThemeVariable("arva-fluo-green")),
      getLineWidth: () => 2,
      lineWidthMinPixels: 2,
    })

    const calculateExtentBounds = fieldBoundData => {
      if (!fieldBoundData || !fieldBoundData.features) return {}

      const allCoordinates = fieldBoundData.features.flatMap(feature => {
        if (feature.geometry.type === "MultiPolygon") {
          return feature.geometry.coordinates.flatMap(polygon => polygon[0])
        } else if (feature.geometry.type === "Polygon") {
          return feature.geometry.coordinates[0]
        }
        return []
      })

      const longVals = allCoordinates.map(coord => coord[0])
      const latVals = allCoordinates.map(coord => coord[1])

      const minLatitude = Math.min.apply(null, latVals)
      const maxLatitude = Math.max.apply(null, latVals)
      const minLongitude = Math.min.apply(null, longVals)
      const maxLongitude = Math.max.apply(null, longVals)

      return { minLatitude, maxLatitude, minLongitude, maxLongitude }
    }

    const b = calculateExtentBounds(fieldBoundaries)
    if (b.minLatitude) {
      this.fitBoundsRotated(
        [[b.minLongitude, b.minLatitude],[b.maxLongitude, b.maxLatitude]],
        { bearing: -1, padding: 0 },
        null,
        map
      )
    }

    map.on("load", () => {
      map.addLayer(fieldBoundariesLayer)
      map.resize()
    })
  }
}
</script>

<style>
@page grower-report {
  size: 8.5in 11in !important;
  margin-left: 0in;
  margin-right: 0in;
  margin-top: 0in;
  margin-bottom: 0.25in;
  @bottom-left {
    content: "Confidential";
    font-size: 0.8rem;
    color: var(--arva-black);
    background-color: var(--arva-cream);
    font-family: 'FK Grotesk', sans-serif;
    padding-left: 8px;
  }
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font-size: 0.8rem;
    color: var(--arva-black);
    background-color: var(--arva-cream);
    font-family: 'FK Grotesk', sans-serif;
    padding-right: 8px;

  }
  @bottom-center {
    font-weight: 400;
    font-size: 0.8rem;
    font-family: 'FK Grotesk', sans-serif;
    color: var(--arva-black);
    background-color: var(--arva-cream);
    content: "© 2025 Arva®";
  }
}
</style>

<style scoped>
.report-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: var(--arva-cream);
  color: var(--arva-black);
}

.pdf-wrapper {
  width: 1000px;
  color: var(--arva-black) !important;
  font-family: 'Inter', sans-serif !important;
  padding-left: 15px;
  padding-right: 15px;
  page: grower-report;
  padding-top: 70px;
  box-decoration-break: clone !important; /* clones the header */
}

.pdf-body {
  page: grower-report;
  width: 1000px;
}

.fill-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.header-info-line {
  font-size: 0.8rem;
  font-weight: 500;
}

.header-label {
  font-weight: 600;
  color: var(--arva-black);
}

.header-separator {
  margin: 0 8px;
  color: var(--arva-black);
  opacity: 0.6;
}

.header-info-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.header-item {
  display: flex;
  flex-direction: column;
  padding-right: 8px;
  margin-right: 8px;
  border-right: 1px solid rgba(0,0,0,0.25);
}
.header-item:last-child {
  border-right: none;
  padding-right: 0;
  margin-right: 0;
}
.header-value {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.1;
  color: var(--arva-black);
}
.header-subtitle {
  font-size: 0.65rem;
  line-height: 1;
  color: var(--arva-black);
  opacity: 0.75;
  margin-top: 2px;
}

.map-wrapper {
  width: 100%;
  height: 250px;
  position: relative;
  background: #1b1b1d;
  overflow: hidden;
}

.top-grower-logo {
  height: 30px;
}

.report-title {
  padding-left: 20px;
  font-size: 1.30rem;
}

.subtitle-1 {
  font-size: 0.9rem;
}

.subtitle-2 {
  font-size: 0.8rem;
  break-after: avoid;
}

.body-2 {
  font-size: 0.875rem;
}

.acreage-table,
.crop-table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.75rem;
  border: 2px solid #ccc;
  border-radius: 5px;
  overflow: hidden;
}

.acreage-table {
  width: 100%;
  max-width: 600px;
}

.crop-table {
  width: 100%;
  min-width: 280px;
}

.acreage-table th,
.acreage-table td,
.crop-table th,
.crop-table td {
  padding: 4px 6px;
  border: none;
}

.region-header,
.acres-header {
  filter: brightness(0.85);
  color: var(--arva-black);
  font-weight: bold;
}

.region-header {
  background-color: var(--arva-cream);
  text-align: left;
}

.acres-header {
  background-color: var(--arva-peach);
  text-align: right;
}

.region-column {
  background-color: var(--arva-cream);
  text-align: left;
}

.acres-column {
  background-color: var(--arva-peach);
  text-align: right;
}

.section-title {
  font-family: "FK Grotesk", sans-serif;
  font-weight: bold;
  font-size: 1.2rem;
  break-after: avoid;
}

.section-text {
  break-after: avoid;
}

.avoid-break {
  break-inside: avoid;
  page-break-inside: avoid;
  -webkit-column-break-inside: avoid;
  -webkit-region-break-inside: avoid;
}

.page-break-after {
  break-after: page;
  page-break-after: always;
}

.hybrid-chart-row {
  display: flex;
  align-items: flex-start;
}

.hybrid-stats-table {
  border-collapse: collapse;
  font-size: 0.7rem;
  min-width: 100%;
}
.hybrid-stats-table th,
.hybrid-stats-table td {
  padding: 2px 6px;
  border: 1px solid #ccc;
  text-align: right;
}
.hybrid-stats-table th:first-child,
.hybrid-stats-table td:first-child {
  text-align: left;
  background: var(--arva-light-green);
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hybrid-stats-table thead th {
  background: var(--arva-light-green);
  font-weight: 600;
}

.ci-stats-table {
  border-collapse: collapse;
  font-size: 0.7rem;
  width: 100%;
}
.ci-stats-table td {
  padding: 3px 6px;
  border: 1px solid #ccc;
}
.ci-stats-table .stat-name {
  text-align: left;
  background: var(--arva-cream);
  font-weight: 500;
  width: 40%;
}
.ci-stats-table .stat-value {
  text-align: right;
  background: #fff;
}

.soil-table {
  border-collapse: collapse;
  font-size: 0.75rem;
  min-width: 100%;
  margin-top: 10px;
}
.soil-table th,
.soil-table td {
  padding: 4px 6px;
  border: 1px solid #ccc;
  text-align: right;
}
.soil-table th:first-child,
.soil-table td:first-child {
  text-align: left;
  background: var(--arva-light-green);
  font-weight: 600;
}
.soil-table thead th {
  background: var(--arva-light-green);
  font-weight: 600;
}

.payment-summary-card {
  background: var(--arva-dark-green);
  border: 1px solid #333;
  border-radius: 8px;
  padding: 4px;
  display: flex;
  flex-direction: column;
}

.payment-crop-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: white;
  text-align: center;
  margin-bottom: 8px;
}

.payment-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.payment-value {
  font-weight: 500;
  font-size: 0.8rem;
  color: var(--arva-fluo-green);
}

.payment-label {
  font-size: 0.6rem;
  color: white;
  opacity: 0.8;
  text-align: center;
}

.practice-chip {
  background: var(--arva-light-green);
  border: 2px solid var(--arva-dark-green);
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 140px;
}

.practice-name {
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--arva-black);
  text-align: center;
  line-height: 1.2;
}

.practice-acres {
  font-size: 0.65rem;
  font-weight: 500;
  color: var(--arva-black);
}

.practice-percent {
  font-size: 0.6rem;
  color: var(--arva-black);
}

.agt-chart-row {
  display: flex;
  align-items: flex-start;
}
.agt-chip {
  border-radius: 14px;
  padding: 6px 10px;
  min-width: 90px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
  line-height: 1.2;
}
.agt-chip-label {
  font-weight: 600;
  text-align: center;
}
.agt-chip-acres {
  text-align: center;
  font-weight: 500;
  opacity: 0.85;
}

.axis-label {
  font-style: italic;
  font-size: 0.7rem;
  color: var(--arva-black);
  opacity: 0.8;
}

.carbon-subtitle {
  font-family: "FK Grotesk", sans-serif;
  font-weight: 400;
  font-size: 0.9rem;
  text-transform: none;
  letter-spacing: 0;
}
.carbon-row {
  font-size: 0.7rem;
}
.carbon-practice {
  font-weight: 500;
  margin-bottom: 0;
}
.carbon-meter {
  width: 100%;
  height: 20px;
}

.carbon-label {
  width: auto;
  text-align: right;
  font-weight: 600;
  white-space: nowrap;
  color: var(--arva-black);
}
.carbon-meter::-webkit-meter-optimum-value {
  background: var(--arva-dark-green);
}
.carbon-meter::-moz-meter-bar {
  background: var(--arva-dark-green);
}
.agt-breakdown-chip {
  position: relative;
}

.agt-breakdown-header .agt-chip-label {
  font-size: 0.7rem;
  font-weight: 600;
}
.agt-breakdown-stats {
  display: flex;
  width: 100%;
  justify-content: space-between;
}
.agt-breakdown-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.agt-breakdown-stat .label {
  font-size: 0.55rem;
  opacity: 0.75;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: 100%;
  text-align: center;
  min-height: 0.75rem;
}
.agt-breakdown-stat .value {
  font-size: 0.7rem;
  font-weight: 600;
  width: 100%;
  text-align: center;
  margin-top: 2px;
}

.boxplot-tutorial-box {
  background-color: var(--arva-cream);
  border: 2px solid #ccc;
  border-radius: 8px;
  width: 66.67%;
  max-width: 650px;
}

.boxplot-tutorial-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--arva-black);
  text-align: center;
}

.boxplot-tutorial-content {
  font-size: 0.8rem;
  color: var(--arva-black);
  line-height: 1.4;
}

.boxplot-tutorial-list {
  margin: 8px 0;
  padding-left: 20px;
}

.boxplot-tutorial-list li {
  margin-bottom: 4px;
}
</style>
